/**
 * Admin Store for RepuCall
 *
 * This store manages all admin-related data including customers, promotions,
 * audit logs, notes, and analytics. All data is persisted via Supabase.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Logger, LogCategory, logDataOperation } from '@/utils/logger';
import { supabase } from '@/lib/supabase';
import type {
  Company,
  CompanySettings,
  User,
  AuditLog,
  CustomerNote,
  Promotion,
  PromotionApplication,
  CustomPlan,
  AdminStats,
  AuditAction,
} from '@/types';

// ============================================================================
// Helpers: DB row <-> App type transformations
// ============================================================================

function dbRowToCompany(row: Record<string, unknown>): Company {
  return {
    id: row.id as string,
    name: row.name as string,
    domain: row.domain as string,
    credits: row.credits as number,
    plan: row.plan as Company['plan'],
    isInternal: row.is_internal as boolean | undefined,
    industry: row.industry as string | undefined,
    organizationSize: row.organization_size as string | undefined,
    phoneNumber: row.phone_number as string | undefined,
    hasUsedTrial: row.has_used_trial as boolean | undefined,
    trialEndsAt: row.trial_ends_at as string | undefined,
    stripeCustomerId: row.stripe_customer_id as string | undefined,
    stripeSubscriptionId: row.stripe_subscription_id as string | undefined,
    address: row.address_street
      ? {
          street: row.address_street as string,
          city: row.address_city as string,
          state: row.address_state as string,
          zipCode: row.address_zip_code as string,
          country: row.address_country as string,
        }
      : undefined,
    billingEmail: row.billing_email as string | undefined,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string | undefined,
    settings: {
      notificationFrequency: (row.notification_frequency as CompanySettings['notificationFrequency']) || 'daily',
      emailNotifications: row.email_notifications as boolean ?? true,
      notifyOnBillingChanges: row.notify_on_billing_changes as boolean | undefined,
      notifyOnPhoneFlagged: row.notify_on_phone_flagged as boolean | undefined,
      notifyOnCreditsLow: row.notify_on_credits_low as boolean | undefined,
      lowCreditThreshold: (row.low_credit_threshold as number) ?? 100,
    },
  };
}

function companyToDbRow(company: Partial<Company>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (company.name !== undefined) row.name = company.name;
  if (company.domain !== undefined) row.domain = company.domain;
  if (company.credits !== undefined) row.credits = company.credits;
  if (company.plan !== undefined) row.plan = company.plan;
  if (company.isInternal !== undefined) row.is_internal = company.isInternal;
  if (company.industry !== undefined) row.industry = company.industry;
  if (company.organizationSize !== undefined) row.organization_size = company.organizationSize;
  if (company.phoneNumber !== undefined) row.phone_number = company.phoneNumber;
  if (company.hasUsedTrial !== undefined) row.has_used_trial = company.hasUsedTrial;
  if (company.trialEndsAt !== undefined) row.trial_ends_at = company.trialEndsAt;
  if (company.stripeCustomerId !== undefined) row.stripe_customer_id = company.stripeCustomerId;
  if (company.stripeSubscriptionId !== undefined) row.stripe_subscription_id = company.stripeSubscriptionId;
  if (company.billingEmail !== undefined) row.billing_email = company.billingEmail;
  if (company.address) {
    row.address_street = company.address.street;
    row.address_city = company.address.city;
    row.address_state = company.address.state;
    row.address_zip_code = company.address.zipCode;
    row.address_country = company.address.country;
  }
  if (company.settings) {
    row.notification_frequency = company.settings.notificationFrequency;
    row.email_notifications = company.settings.emailNotifications;
    row.notify_on_billing_changes = company.settings.notifyOnBillingChanges;
    row.notify_on_phone_flagged = company.settings.notifyOnPhoneFlagged;
    row.notify_on_credits_low = company.settings.notifyOnCreditsLow;
    row.low_credit_threshold = company.settings.lowCreditThreshold;
  }
  return row;
}

function dbRowToUser(row: Record<string, unknown>): User {
  return {
    id: row.id as string,
    email: row.email as string,
    name: row.name as string,
    role: row.role as User['role'],
    companyId: row.company_id as string,
    avatarUrl: row.avatar_url as string | undefined,
    authProvider: row.auth_provider as User['authProvider'],
    isActive: row.is_active as boolean,
    isSuperAdmin: row.is_super_admin as boolean | undefined,
    emailVerified: row.email_verified as boolean | undefined,
    lastLoginAt: row.last_login_at as string | undefined,
    createdAt: row.created_at as string,
  };
}

function dbRowToAuditLog(row: Record<string, unknown>): AuditLog {
  return {
    id: row.id as string,
    adminUserId: row.admin_user_id as string,
    adminUserName: (row as Record<string, unknown>).admin_user_name as string || '',
    adminUserEmail: (row as Record<string, unknown>).admin_user_email as string || '',
    action: row.action as AuditAction,
    targetType: row.target_type as AuditLog['targetType'],
    targetId: row.target_id as string,
    targetName: row.target_name as string | undefined,
    details: (row.details as Record<string, unknown>) || {},
    ipAddress: row.ip_address as string | undefined,
    userAgent: row.user_agent as string | undefined,
    createdAt: row.created_at as string,
  };
}

function dbRowToNote(row: Record<string, unknown>): CustomerNote {
  return {
    id: row.id as string,
    companyId: row.company_id as string,
    authorId: row.author_id as string,
    authorName: (row as Record<string, unknown>).author_name as string || '',
    content: row.content as string,
    isInternal: row.is_internal as boolean,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function dbRowToPromotion(row: Record<string, unknown>): Promotion {
  return {
    id: row.id as string,
    code: row.code as string,
    name: row.name as string,
    description: row.description as string,
    type: row.type as Promotion['type'],
    value: row.value as number,
    isActive: row.is_active as boolean,
    appliedCount: row.applied_count as number,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function dbRowToPromotionApplication(row: Record<string, unknown>): PromotionApplication {
  return {
    id: row.id as string,
    promotionId: row.promotion_id as string,
    promotionCode: (row as Record<string, unknown>).promotion_code as string || '',
    companyId: row.company_id as string,
    companyName: (row as Record<string, unknown>).company_name as string || '',
    creditsGranted: row.credits_granted as number,
    appliedBy: row.applied_by_user_id as string,
    appliedAt: row.created_at as string,
  };
}

// ============================================================================
// Types
// ============================================================================

interface AdminState {
  // Data
  allCompanies: Company[];
  allUsers: User[];
  auditLogs: AuditLog[];
  customerNotes: Record<string, CustomerNote[]>; // companyId -> notes
  promotions: Promotion[];
  promotionApplications: PromotionApplication[];
  customPlans: Record<string, CustomPlan>; // companyId -> plan
  adminStats: AdminStats | null;

  // UI State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  impersonatedCompanyId: string | null;

  // Actions
  fetchAllCompanies: () => Promise<void>;
  fetchCompanyById: (companyId: string) => Promise<Company | null>;
  createCompany: (company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Company>;
  updateCompany: (companyId: string, updates: Partial<Company>) => Promise<void>;
  deactivateCompany: (companyId: string) => Promise<void>;
  activateCompany: (companyId: string) => Promise<void>;
  toggleInternalAccount: (companyId: string) => Promise<void>;

  // User Management
  fetchUsersByCompany: (companyId: string) => Promise<User[]>;

  // Billing & Credits
  addCredits: (companyId: string, amount: number, reason: string) => Promise<void>;
  changePlan: (companyId: string, plan: 'trial' | 'starter' | 'growth' | 'pro' | 'enterprise') => Promise<void>;
  createCustomPlan: (companyId: string, plan: Omit<CustomPlan, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Promotions
  fetchPromotions: () => Promise<void>;
  createPromotion: (promo: Omit<Promotion, 'id' | 'appliedCount' | 'createdAt' | 'updatedAt'>) => Promise<Promotion>;
  updatePromotion: (id: string, updates: Partial<Promotion>) => Promise<void>;
  deletePromotion: (id: string) => Promise<void>;
  applyPromotion: (promotionId: string, companyId: string) => Promise<void>;
  fetchPromotionApplications: () => Promise<void>;

  // Customer Notes
  fetchNotes: (companyId: string) => Promise<void>;
  addNote: (companyId: string, content: string, isInternal: boolean) => Promise<void>;
  updateNote: (noteId: string, companyId: string, content: string) => Promise<void>;
  deleteNote: (noteId: string, companyId: string) => Promise<void>;

  // Audit Log
  fetchAuditLogs: (filters?: { companyId?: string; action?: AuditAction; limit?: number }) => Promise<void>;
  logAuditAction: (action: AuditAction, targetType: 'customer' | 'user' | 'billing' | 'promotion', targetId: string, targetName: string, details: Record<string, unknown>) => void;

  // Analytics
  fetchAdminStats: () => Promise<void>;
  exportReport: (type: 'credit_usage' | 'api_usage') => Promise<Blob>;

  // Impersonation
  impersonateCompany: (companyId: string) => Promise<void>;
  stopImpersonation: () => void;

  // Utility
  clearError: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useAdminStore = create<AdminState>()(
  devtools(
    (set, get) => ({
      // ========================================================================
      // Initial State
      // ========================================================================
      allCompanies: [],
      allUsers: [],
      auditLogs: [],
      customerNotes: {},
      promotions: [],
      promotionApplications: [],
      customPlans: {},
      adminStats: null,
      isLoading: false,
      isSaving: false,
      error: null,
      impersonatedCompanyId: null,

      // ========================================================================
      // Fetch All Companies
      // ========================================================================
      fetchAllCompanies: async () => {
        Logger.info(LogCategory.DATA, 'Fetching all companies', 'fetchAllCompanies');
        set({ isLoading: true, error: null });

        try {
          const { data, error } = await (supabase.from('companies') as any)
            .select('*')
            .order('created_at', { ascending: false });

          if (error) throw error;

          const companies: Company[] = (data || []).map(dbRowToCompany);
          set({ allCompanies: companies, isLoading: false });
          logDataOperation('fetch', 'companies', companies.length);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch companies';
          Logger.error(LogCategory.DATA, errorMessage, 'fetchAllCompanies', undefined, error);
          set({ isLoading: false, error: errorMessage });
          logDataOperation('fetch', 'companies', undefined, { errorMessage }, error);
        }
      },

      // ========================================================================
      // Fetch Company By ID
      // ========================================================================
      fetchCompanyById: async (companyId) => {
        Logger.info(LogCategory.DATA, `Fetching company: ${companyId}`, 'fetchCompanyById');

        try {
          const { data, error } = await (supabase.from('companies') as any)
            .select('*')
            .eq('id', companyId)
            .single();

          if (error) throw error;
          if (!data) return null;

          const company = dbRowToCompany(data);

          get().logAuditAction('customer.view', 'customer', companyId, company.name, {});

          return company;
        } catch (error) {
          Logger.error(LogCategory.DATA, 'Failed to fetch company', 'fetchCompanyById', undefined, error);
          return null;
        }
      },

      // ========================================================================
      // Create Company
      // ========================================================================
      createCompany: async (companyData) => {
        Logger.info(LogCategory.DATA, 'Creating new company', 'createCompany');
        set({ isSaving: true, error: null });

        try {
          const row = companyToDbRow(companyData);

          const { data, error } = await (supabase.from('companies') as any)
            .insert(row)
            .select()
            .single();

          if (error) throw error;

          const newCompany = dbRowToCompany(data);

          // Update local state
          set({ allCompanies: [newCompany, ...get().allCompanies], isSaving: false });

          get().logAuditAction('customer.create', 'customer', newCompany.id, newCompany.name, {
            plan: newCompany.plan,
            credits: newCompany.credits,
            isInternal: newCompany.isInternal,
          });
          logDataOperation('create', 'company', 1);

          return newCompany;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create company';
          Logger.error(LogCategory.DATA, errorMessage, 'createCompany', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Update Company
      // ========================================================================
      updateCompany: async (companyId, updates) => {
        Logger.info(LogCategory.DATA, `Updating company: ${companyId}`, 'updateCompany');
        set({ isSaving: true, error: null });

        try {
          const row = companyToDbRow(updates);
          row.updated_at = new Date().toISOString();

          const { data, error } = await (supabase.from('companies') as any)
            .update(row)
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);

          // Update local state
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies, isSaving: false });

          get().logAuditAction('customer.edit', 'customer', companyId, updatedCompany.name, updates);
          logDataOperation('update', 'company', 1);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update company';
          Logger.error(LogCategory.DATA, errorMessage, 'updateCompany', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Deactivate Company
      // ========================================================================
      deactivateCompany: async (companyId) => {
        Logger.info(LogCategory.DATA, `Deactivating company: ${companyId}`, 'deactivateCompany');

        try {
          const { data, error } = await (supabase.from('companies') as any)
            .update({ plan: 'trial', credits: 0, updated_at: new Date().toISOString() })
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies });

          get().logAuditAction('customer.deactivate', 'customer', companyId, updatedCompany.name, {});
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate company';
          Logger.error(LogCategory.DATA, errorMessage, 'deactivateCompany', undefined, error);
          set({ error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Activate Company
      // ========================================================================
      activateCompany: async (companyId) => {
        Logger.info(LogCategory.DATA, `Activating company: ${companyId}`, 'activateCompany');

        try {
          const { data, error } = await (supabase.from('companies') as any)
            .update({ plan: 'starter', updated_at: new Date().toISOString() })
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies });

          get().logAuditAction('customer.activate', 'customer', companyId, updatedCompany.name, {});
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to activate company';
          Logger.error(LogCategory.DATA, errorMessage, 'activateCompany', undefined, error);
          set({ error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Toggle Internal Account
      // ========================================================================
      toggleInternalAccount: async (companyId) => {
        Logger.info(LogCategory.DATA, `Toggling internal account status: ${companyId}`, 'toggleInternalAccount');
        set({ isSaving: true, error: null });

        try {
          // Fetch the current value
          const { data: current, error: fetchError } = await (supabase.from('companies') as any)
            .select('is_internal, name')
            .eq('id', companyId)
            .single();

          if (fetchError) throw fetchError;

          const newStatus = !current.is_internal;

          const { data, error } = await (supabase.from('companies') as any)
            .update({ is_internal: newStatus, updated_at: new Date().toISOString() })
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies, isSaving: false });

          get().logAuditAction(
            'customer.edit',
            'customer',
            companyId,
            updatedCompany.name,
            {
              field: 'isInternal',
              oldValue: current.is_internal || false,
              newValue: newStatus,
            }
          );

          Logger.info(LogCategory.DATA, `Company ${companyId} internal status: ${newStatus}`, 'toggleInternalAccount');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to toggle internal status';
          Logger.error(LogCategory.DATA, errorMessage, 'toggleInternalAccount', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Fetch Users By Company
      // ========================================================================
      fetchUsersByCompany: async (companyId) => {
        Logger.info(LogCategory.DATA, `Fetching users for company: ${companyId}`, 'fetchUsersByCompany');

        try {
          const { data, error } = await (supabase.from('users') as any)
            .select('*')
            .eq('company_id', companyId);

          if (error) throw error;

          const users: User[] = (data || []).map(dbRowToUser);
          return users;
        } catch (error) {
          Logger.error(LogCategory.DATA, 'Failed to fetch users', 'fetchUsersByCompany', undefined, error);
          return [];
        }
      },

      // ========================================================================
      // Add Credits
      // ========================================================================
      addCredits: async (companyId, amount, reason) => {
        Logger.info(LogCategory.DATA, `Adding ${amount} credits to company: ${companyId}`, 'addCredits');
        set({ isSaving: true, error: null });

        try {
          // Get current credits
          const { data: current, error: fetchError } = await (supabase.from('companies') as any)
            .select('credits, name')
            .eq('id', companyId)
            .single();

          if (fetchError) throw fetchError;

          const previousBalance = current.credits as number;
          const newBalance = previousBalance + amount;

          const { data, error } = await (supabase.from('companies') as any)
            .update({ credits: newBalance, updated_at: new Date().toISOString() })
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies, isSaving: false });

          get().logAuditAction('billing.credit_add', 'billing', companyId, current.name as string, {
            amount,
            reason,
            previousBalance,
            newBalance,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to add credits';
          Logger.error(LogCategory.DATA, errorMessage, 'addCredits', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Change Plan
      // ========================================================================
      changePlan: async (companyId, plan) => {
        Logger.info(LogCategory.DATA, `Changing plan for company ${companyId} to ${plan}`, 'changePlan');
        set({ isSaving: true, error: null });

        try {
          // Get current plan for audit log
          const { data: current, error: fetchError } = await (supabase.from('companies') as any)
            .select('plan, name')
            .eq('id', companyId)
            .single();

          if (fetchError) throw fetchError;

          const { data, error } = await (supabase.from('companies') as any)
            .update({ plan, updated_at: new Date().toISOString() })
            .eq('id', companyId)
            .select()
            .single();

          if (error) throw error;

          const updatedCompany = dbRowToCompany(data);
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? updatedCompany : c
          );
          set({ allCompanies: companies, isSaving: false });

          get().logAuditAction('billing.plan_change', 'billing', companyId, current.name as string, {
            previousPlan: current.plan,
            newPlan: plan,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to change plan';
          Logger.error(LogCategory.DATA, errorMessage, 'changePlan', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Create Custom Plan
      // ========================================================================
      createCustomPlan: async (companyId, plan) => {
        Logger.info(LogCategory.DATA, `Creating custom plan for company: ${companyId}`, 'createCustomPlan');
        set({ isSaving: true, error: null });

        try {
          // Custom plans are stored locally in state since there is no
          // dedicated custom_plans table specified in the DB schema.
          const now = new Date().toISOString();
          const customPlan: CustomPlan = {
            id: `cplan_${Date.now()}`,
            companyId,
            ...plan,
            createdAt: now,
            updatedAt: now,
          };

          const customPlans = { ...get().customPlans, [companyId]: customPlan };
          set({ customPlans, isSaving: false });

          const company = get().allCompanies.find((c) => c.id === companyId);
          get().logAuditAction('billing.plan_change', 'billing', companyId, company?.name || '', {
            planType: 'custom',
            planDetails: plan,
          });

          logDataOperation('create', 'customPlan', 1);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create custom plan';
          Logger.error(LogCategory.DATA, errorMessage, 'createCustomPlan', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Fetch Promotions
      // ========================================================================
      fetchPromotions: async () => {
        Logger.info(LogCategory.DATA, 'Fetching promotions', 'fetchPromotions');
        set({ isLoading: true, error: null });

        try {
          const { data, error } = await (supabase.from('promotions') as any)
            .select('*')
            .order('created_at', { ascending: false });

          if (error) throw error;

          const promotions: Promotion[] = (data || []).map(dbRowToPromotion);
          set({ promotions, isLoading: false });
          logDataOperation('fetch', 'promotions', promotions.length);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch promotions';
          Logger.error(LogCategory.DATA, errorMessage, 'fetchPromotions', undefined, error);
          set({ isLoading: false, error: errorMessage });
        }
      },

      // ========================================================================
      // Create Promotion
      // ========================================================================
      createPromotion: async (promo) => {
        Logger.info(LogCategory.DATA, 'Creating promotion', 'createPromotion');
        set({ isSaving: true, error: null });

        try {
          const row = {
            code: promo.code,
            name: promo.name,
            description: promo.description,
            type: promo.type,
            value: promo.value,
            is_active: promo.isActive,
            applied_count: 0,
          };

          const { data, error } = await (supabase.from('promotions') as any)
            .insert(row)
            .select()
            .single();

          if (error) throw error;

          const newPromotion = dbRowToPromotion(data);
          set({ promotions: [newPromotion, ...get().promotions], isSaving: false });

          get().logAuditAction('promotion.create', 'promotion', newPromotion.id, newPromotion.name, promo as unknown as Record<string, unknown>);
          logDataOperation('create', 'promotion', 1);

          return newPromotion;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create promotion';
          Logger.error(LogCategory.DATA, errorMessage, 'createPromotion', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Update Promotion
      // ========================================================================
      updatePromotion: async (id, updates) => {
        Logger.info(LogCategory.DATA, `Updating promotion: ${id}`, 'updatePromotion');
        set({ isSaving: true, error: null });

        try {
          const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
          if (updates.code !== undefined) row.code = updates.code;
          if (updates.name !== undefined) row.name = updates.name;
          if (updates.description !== undefined) row.description = updates.description;
          if (updates.type !== undefined) row.type = updates.type;
          if (updates.value !== undefined) row.value = updates.value;
          if (updates.isActive !== undefined) row.is_active = updates.isActive;
          if (updates.appliedCount !== undefined) row.applied_count = updates.appliedCount;

          const { data, error } = await (supabase.from('promotions') as any)
            .update(row)
            .eq('id', id)
            .select()
            .single();

          if (error) throw error;

          const updatedPromo = dbRowToPromotion(data);
          const promotions = get().promotions.map((p) => (p.id === id ? updatedPromo : p));
          set({ promotions, isSaving: false });

          get().logAuditAction('promotion.edit', 'promotion', id, updatedPromo.name, updates as unknown as Record<string, unknown>);
          logDataOperation('update', 'promotion', 1);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update promotion';
          Logger.error(LogCategory.DATA, errorMessage, 'updatePromotion', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Delete Promotion
      // ========================================================================
      deletePromotion: async (id) => {
        Logger.info(LogCategory.DATA, `Deleting promotion: ${id}`, 'deletePromotion');
        set({ isSaving: true, error: null });

        try {
          const promo = get().promotions.find((p) => p.id === id);

          const { error } = await (supabase.from('promotions') as any)
            .delete()
            .eq('id', id);

          if (error) throw error;

          const updatedPromotions = get().promotions.filter((p) => p.id !== id);
          set({ promotions: updatedPromotions, isSaving: false });

          get().logAuditAction('promotion.delete', 'promotion', id, promo?.name || '', {});
          logDataOperation('delete', 'promotion', 1);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete promotion';
          Logger.error(LogCategory.DATA, errorMessage, 'deletePromotion', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Apply Promotion
      // ========================================================================
      applyPromotion: async (promotionId, companyId) => {
        Logger.info(LogCategory.DATA, `Applying promotion ${promotionId} to company ${companyId}`, 'applyPromotion');
        set({ isSaving: true, error: null });

        try {
          const promotion = get().promotions.find((p) => p.id === promotionId);
          const company = get().allCompanies.find((c) => c.id === companyId);

          if (!promotion) throw new Error('Promotion not found');
          if (!company) throw new Error('Company not found');
          if (!promotion.isActive) throw new Error('Promotion is not active');

          // Get current user
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const appliedByUserId = authUser?.id || 'unknown';

          // Insert promotion application
          const { error: appError } = await (supabase.from('promotion_applications') as any)
            .insert({
              promotion_id: promotionId,
              company_id: companyId,
              applied_by_user_id: appliedByUserId,
              credits_granted: promotion.value,
            });

          if (appError) throw appError;

          // Update company credits
          const newCredits = company.credits + promotion.value;
          const { error: creditError } = await (supabase.from('companies') as any)
            .update({ credits: newCredits, updated_at: new Date().toISOString() })
            .eq('id', companyId);

          if (creditError) throw creditError;

          // Update promotion applied count
          const { error: promoError } = await (supabase.from('promotions') as any)
            .update({ applied_count: promotion.appliedCount + 1, updated_at: new Date().toISOString() })
            .eq('id', promotionId);

          if (promoError) throw promoError;

          // Update local state
          const companies = get().allCompanies.map((c) =>
            c.id === companyId ? { ...c, credits: newCredits } : c
          );
          const promotions = get().promotions.map((p) =>
            p.id === promotionId ? { ...p, appliedCount: p.appliedCount + 1 } : p
          );
          set({ allCompanies: companies, promotions, isSaving: false });

          get().logAuditAction('promotion.apply', 'promotion', promotionId, promotion.name, {
            companyId,
            companyName: company.name,
            creditsGranted: promotion.value,
          });

          logDataOperation('create', 'promotionApplication', 1);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to apply promotion';
          Logger.error(LogCategory.DATA, errorMessage, 'applyPromotion', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Fetch Promotion Applications
      // ========================================================================
      fetchPromotionApplications: async () => {
        Logger.info(LogCategory.DATA, 'Fetching promotion applications', 'fetchPromotionApplications');

        try {
          const { data, error } = await (supabase.from('promotion_applications') as any)
            .select('*, promotions(code), companies(name)');

          if (error) throw error;

          const applications: PromotionApplication[] = (data || []).map((row: Record<string, unknown>) => ({
            id: row.id as string,
            promotionId: row.promotion_id as string,
            promotionCode: (row.promotions as Record<string, unknown>)?.code as string || '',
            companyId: row.company_id as string,
            companyName: (row.companies as Record<string, unknown>)?.name as string || '',
            creditsGranted: row.credits_granted as number,
            appliedBy: row.applied_by_user_id as string,
            appliedAt: row.created_at as string,
          }));

          set({ promotionApplications: applications });
        } catch (error) {
          Logger.error(LogCategory.DATA, 'Failed to fetch promotion applications', 'fetchPromotionApplications', undefined, error);
        }
      },

      // ========================================================================
      // Fetch Notes
      // ========================================================================
      fetchNotes: async (companyId) => {
        Logger.info(LogCategory.DATA, `Fetching notes for company: ${companyId}`, 'fetchNotes');

        try {
          const { data, error } = await (supabase.from('customer_notes') as any)
            .select('*, users(name)')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

          if (error) throw error;

          const notes: CustomerNote[] = (data || []).map((row: Record<string, unknown>) => ({
            id: row.id as string,
            companyId: row.company_id as string,
            authorId: row.author_id as string,
            authorName: (row.users as Record<string, unknown>)?.name as string || '',
            content: row.content as string,
            isInternal: row.is_internal as boolean,
            createdAt: row.created_at as string,
            updatedAt: row.updated_at as string,
          }));

          set({ customerNotes: { ...get().customerNotes, [companyId]: notes } });
        } catch (error) {
          Logger.error(LogCategory.DATA, 'Failed to fetch notes', 'fetchNotes', undefined, error);
        }
      },

      // ========================================================================
      // Add Note
      // ========================================================================
      addNote: async (companyId, content, isInternal) => {
        Logger.info(LogCategory.DATA, `Adding note to company: ${companyId}`, 'addNote');
        set({ isSaving: true, error: null });

        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const authorId = authUser?.id || 'unknown';

          const { data, error } = await (supabase.from('customer_notes') as any)
            .insert({
              company_id: companyId,
              author_id: authorId,
              content,
              is_internal: isInternal,
            })
            .select('*, users(name)')
            .single();

          if (error) throw error;

          const note: CustomerNote = {
            id: data.id as string,
            companyId: data.company_id as string,
            authorId: data.author_id as string,
            authorName: (data.users as Record<string, unknown>)?.name as string || '',
            content: data.content as string,
            isInternal: data.is_internal as boolean,
            createdAt: data.created_at as string,
            updatedAt: data.updated_at as string,
          };

          const existingNotes = get().customerNotes[companyId] || [];
          set({
            customerNotes: { ...get().customerNotes, [companyId]: [note, ...existingNotes] },
            isSaving: false,
          });

          const company = get().allCompanies.find((c) => c.id === companyId);
          get().logAuditAction('note.create', 'customer', companyId, company?.name || '', { content, isInternal });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to add note';
          Logger.error(LogCategory.DATA, errorMessage, 'addNote', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Update Note
      // ========================================================================
      updateNote: async (noteId, companyId, content) => {
        Logger.info(LogCategory.DATA, `Updating note: ${noteId}`, 'updateNote');
        set({ isSaving: true, error: null });

        try {
          const { data, error } = await (supabase.from('customer_notes') as any)
            .update({ content, updated_at: new Date().toISOString() })
            .eq('id', noteId)
            .select('*, users(name)')
            .single();

          if (error) throw error;

          const updatedNote: CustomerNote = {
            id: data.id as string,
            companyId: data.company_id as string,
            authorId: data.author_id as string,
            authorName: (data.users as Record<string, unknown>)?.name as string || '',
            content: data.content as string,
            isInternal: data.is_internal as boolean,
            createdAt: data.created_at as string,
            updatedAt: data.updated_at as string,
          };

          const companyNotes = (get().customerNotes[companyId] || []).map((n) =>
            n.id === noteId ? updatedNote : n
          );
          set({
            customerNotes: { ...get().customerNotes, [companyId]: companyNotes },
            isSaving: false,
          });

          const company = get().allCompanies.find((c) => c.id === companyId);
          get().logAuditAction('note.edit', 'customer', companyId, company?.name || '', { noteId, content });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update note';
          Logger.error(LogCategory.DATA, errorMessage, 'updateNote', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Delete Note
      // ========================================================================
      deleteNote: async (noteId, companyId) => {
        Logger.info(LogCategory.DATA, `Deleting note: ${noteId}`, 'deleteNote');
        set({ isSaving: true, error: null });

        try {
          const { error } = await (supabase.from('customer_notes') as any)
            .delete()
            .eq('id', noteId);

          if (error) throw error;

          const updatedNotes = (get().customerNotes[companyId] || []).filter((n) => n.id !== noteId);
          set({
            customerNotes: { ...get().customerNotes, [companyId]: updatedNotes },
            isSaving: false,
          });

          const company = get().allCompanies.find((c) => c.id === companyId);
          get().logAuditAction('note.delete', 'customer', companyId, company?.name || '', { noteId });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete note';
          Logger.error(LogCategory.DATA, errorMessage, 'deleteNote', undefined, error);
          set({ isSaving: false, error: errorMessage });
          throw error;
        }
      },

      // ========================================================================
      // Fetch Audit Logs
      // ========================================================================
      fetchAuditLogs: async (filters = {}) => {
        Logger.info(LogCategory.DATA, 'Fetching audit logs', 'fetchAuditLogs');
        set({ isLoading: true, error: null });

        try {
          let query = (supabase.from('audit_logs') as any)
            .select('*, users(name, email)')
            .order('created_at', { ascending: false });

          if (filters.companyId) {
            query = query.eq('target_id', filters.companyId);
          }
          if (filters.action) {
            query = query.eq('action', filters.action);
          }
          if (filters.limit) {
            query = query.limit(filters.limit);
          }

          const { data, error } = await query;

          if (error) throw error;

          const logs: AuditLog[] = (data || []).map((row: Record<string, unknown>) => ({
            id: row.id as string,
            adminUserId: row.admin_user_id as string,
            adminUserName: (row.users as Record<string, unknown>)?.name as string || '',
            adminUserEmail: (row.users as Record<string, unknown>)?.email as string || '',
            action: row.action as AuditAction,
            targetType: row.target_type as AuditLog['targetType'],
            targetId: row.target_id as string,
            targetName: row.target_name as string | undefined,
            details: (row.details as Record<string, unknown>) || {},
            ipAddress: row.ip_address as string | undefined,
            userAgent: row.user_agent as string | undefined,
            createdAt: row.created_at as string,
          }));

          set({ auditLogs: logs, isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch audit logs';
          Logger.error(LogCategory.DATA, errorMessage, 'fetchAuditLogs', undefined, error);
          set({ isLoading: false, error: errorMessage });
        }
      },

      // ========================================================================
      // Log Audit Action
      // ========================================================================
      logAuditAction: (action, targetType, targetId, targetName, details) => {
        Logger.info(LogCategory.DATA, `Logging audit action: ${action}`, 'logAuditAction');

        // Fire and forget — do not block the caller
        (async () => {
          try {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            const adminUserId = authUser?.id || 'unknown';

            const { error } = await (supabase.from('audit_logs') as any)
              .insert({
                admin_user_id: adminUserId,
                action,
                target_type: targetType,
                target_id: targetId,
                target_name: targetName,
                details,
              });

            if (error) {
              Logger.error(LogCategory.DATA, 'Failed to insert audit log', 'logAuditAction', undefined, error);
            }
          } catch (error) {
            Logger.error(LogCategory.DATA, 'Failed to log audit action', 'logAuditAction', undefined, error);
          }
        })();
      },

      // ========================================================================
      // Fetch Admin Stats
      // ========================================================================
      fetchAdminStats: async () => {
        Logger.info(LogCategory.DATA, 'Fetching admin stats', 'fetchAdminStats');
        set({ isLoading: true, error: null });

        try {
          // Fetch all companies for aggregation
          const { data: companiesData, error: companiesError } = await (supabase.from('companies') as any)
            .select('credits, plan, is_internal, low_credit_threshold');

          if (companiesError) throw companiesError;

          const rows = (companiesData || []) as Record<string, unknown>[];
          const totalCustomers = rows.length;
          const internalAccounts = rows.filter((r) => r.is_internal).length;
          const externalRows = rows.filter((r) => !r.is_internal);
          const externalCustomers = externalRows.length;
          const activeCustomers = rows.filter((r) => (r.plan as string) !== 'trial' || (r.credits as number) > 0).length;

          const totalCreditsIssued = externalRows.reduce((sum, r) => sum + (r.credits as number), 0);
          const avgCreditsPerCustomer = externalCustomers > 0
            ? Math.round(totalCreditsIssued / externalCustomers)
            : 0;
          const customersLowOnCredits = externalRows.filter(
            (r) => (r.credits as number) < ((r.low_credit_threshold as number) || 100)
          ).length;

          // Fetch failed billing count
          const { count: failedBillingCount } = await (supabase.from('billing_history') as any)
            .select('id', { count: 'exact', head: true })
            .eq('status', 'failed');

          // Fetch total revenue
          const { data: revenueData } = await (supabase.from('billing_history') as any)
            .select('amount')
            .eq('status', 'succeeded');

          const totalRevenue = (revenueData || []).reduce(
            (sum: number, r: Record<string, unknown>) => sum + ((r.amount as number) || 0),
            0
          );

          const stats: AdminStats = {
            totalCustomers,
            activeCustomers,
            internalAccounts,
            externalCustomers,
            totalRevenue,
            totalCreditsIssued,
            totalCreditsUsed: 0, // Would need a usage tracking table
            avgCreditsPerCustomer,
            customersLowOnCredits,
            failedBillingCount: failedBillingCount || 0,
          };

          set({ adminStats: stats, isLoading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch admin stats';
          Logger.error(LogCategory.DATA, errorMessage, 'fetchAdminStats', undefined, error);
          set({ isLoading: false, error: errorMessage });
        }
      },

      // ========================================================================
      // Export Report
      // ========================================================================
      exportReport: async (type) => {
        Logger.info(LogCategory.DATA, `Exporting ${type} report`, 'exportReport');

        try {
          // Fetch fresh data for the report
          const { data: companiesData, error } = await (supabase.from('companies') as any)
            .select('id, name, plan, credits, is_internal, low_credit_threshold')
            .eq('is_internal', false)
            .order('name');

          if (error) throw error;

          const rows = (companiesData || []) as Record<string, unknown>[];
          let csvContent = '';

          if (type === 'credit_usage') {
            csvContent = 'Company Name,Company ID,Plan,Credits Remaining,Low Credit Threshold,Status\n';
            rows.forEach((r) => {
              const threshold = (r.low_credit_threshold as number) || 100;
              const credits = r.credits as number;
              const status = credits < threshold ? 'Low' : 'OK';
              csvContent += `"${r.name}","${r.id}","${r.plan}",${credits},${threshold},"${status}"\n`;
            });
          } else if (type === 'api_usage') {
            // Fetch billing history for richer data
            const { data: billingData } = await (supabase.from('billing_history') as any)
              .select('company_id, credits')
              .eq('status', 'succeeded');

            const creditsByCompany: Record<string, number> = {};
            (billingData || []).forEach((b: Record<string, unknown>) => {
              const cid = b.company_id as string;
              creditsByCompany[cid] = (creditsByCompany[cid] || 0) + ((b.credits as number) || 0);
            });

            csvContent = 'Company Name,Company ID,Plan,Credits Used\n';
            rows.forEach((r) => {
              const creditsUsed = creditsByCompany[r.id as string] || 0;
              csvContent += `"${r.name}","${r.id}","${r.plan}",${creditsUsed}\n`;
            });
          }

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          return blob;
        } catch (error) {
          Logger.error(LogCategory.DATA, 'Failed to export report', 'exportReport', undefined, error);
          throw error;
        }
      },

      // ========================================================================
      // Impersonate Company
      // ========================================================================
      impersonateCompany: async (companyId) => {
        Logger.info(LogCategory.DATA, `Impersonating company: ${companyId}`, 'impersonateCompany');

        const company = get().allCompanies.find((c) => c.id === companyId);
        if (company) {
          set({ impersonatedCompanyId: companyId });
          get().logAuditAction('customer.impersonate', 'customer', companyId, company.name, {});
        }
      },

      // ========================================================================
      // Stop Impersonation
      // ========================================================================
      stopImpersonation: () => {
        Logger.info(LogCategory.DATA, 'Stopping impersonation', 'stopImpersonation');
        set({ impersonatedCompanyId: null });
      },

      // ========================================================================
      // Clear Error
      // ========================================================================
      clearError: () => {
        Logger.debug(LogCategory.UI, 'Clearing error state', 'clearError');
        set({ error: null });
      },
    }),
    { name: 'admin-store' }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useAllCompanies = () => useAdminStore((state) => state.allCompanies);
export const useAdminLoading = () => useAdminStore((state) => state.isLoading);
export const useAdminSaving = () => useAdminStore((state) => state.isSaving);
export const useAdminError = () => useAdminStore((state) => state.error);
export const useAdminStats = () => useAdminStore((state) => state.adminStats);
export const useAuditLogs = () => useAdminStore((state) => state.auditLogs);
export const usePromotions = () => useAdminStore((state) => state.promotions);
export const usePromotionApplications = () => useAdminStore((state) => state.promotionApplications);
export const useCustomerNotes = (companyId: string) => useAdminStore((state) => state.customerNotes[companyId] || []);
export const useImpersonatedCompanyId = () => useAdminStore((state) => state.impersonatedCompanyId);
